import { useEffect, useRef } from 'react';

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Explains how to lay out the spreadsheet for {@link rowsToQuestions}. Uses a native <dialog> so
 * Escape-to-close and focus trapping come for free; the state is driven from the parent so the
 * (i) button and the dialog stay in sync.
 */
export function ExcelFormatDialog({ open, onClose }: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="format-dialog"
      onClose={onClose}
      onClick={(event) => {
        // A click landing on the <dialog> itself (rather than its content) is the backdrop.
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="format-dialog-body">
        <h3>Formatting your Excel file</h3>
        <p className="note">
          Put one question per row on the first worksheet. A header row is optional; it's skipped
          automatically.
        </p>

        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>What goes here</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>
                <strong>Question</strong>: the wording the employee sees.
              </td>
            </tr>
            <tr>
              <td>2</td>
              <td>
                <strong>Response type</strong>: one of <code>Open Text</code>, <code>Yes/No</code>,{' '}
                <code>Scale</code> or <code>Multiple Choice</code>.
              </td>
            </tr>
          </tbody>
        </table>

        <p className="note">
          <strong>Grouping questions into sections?</strong> Tick “Include section column” and put
          the section title in column 2. The response type then moves to column 3, and every other
          column shifts one to the right. Leave a section cell blank to keep that question at the top
          level. Consecutive rows that share the same section title are grouped into one section.
        </p>

        <p>
          <strong>For a Scale question</strong>, add these columns:
        </p>
        <table>
          <tbody>
            <tr>
              <td>3</td>
              <td>Low value: a whole number (e.g. 1).</td>
            </tr>
            <tr>
              <td>4</td>
              <td>High value: a whole number (e.g. 5).</td>
            </tr>
            <tr>
              <td>5</td>
              <td>Low label: optional (e.g. “Strongly disagree”).</td>
            </tr>
            <tr>
              <td>6</td>
              <td>High label: optional (e.g. “Strongly agree”).</td>
            </tr>
          </tbody>
        </table>

        <p>
          <strong>For a Multiple Choice question</strong>, every column from column 3 onward is
          taken as one option. Add at least two.
        </p>

        <p className="note">Example:</p>
        <table>
          <thead>
            <tr>
              <th>Question</th>
              <th>Response type</th>
              <th>3</th>
              <th>4</th>
              <th>5</th>
              <th>6</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>What could we do better?</td>
              <td>Open Text</td>
              <td colSpan={4} />
            </tr>
            <tr>
              <td>Would you recommend us?</td>
              <td>Yes/No</td>
              <td colSpan={4} />
            </tr>
            <tr>
              <td>How supported do you feel?</td>
              <td>Scale</td>
              <td>1</td>
              <td>5</td>
              <td>Not at all</td>
              <td>Fully</td>
            </tr>
            <tr>
              <td>Which team are you in?</td>
              <td>Multiple Choice</td>
              <td>Sales</td>
              <td>Support</td>
              <td>Engineering</td>
              <td>Other</td>
            </tr>
          </tbody>
        </table>

        <p className="note">
          The same questions with the “Include section column” option: the section title sits in
          column 2 and everything else shifts one column right. The two “Your manager” rows are
          consecutive, so they become one section; the blank section cell keeps the last question at
          the top level:
        </p>
        <table>
          <thead>
            <tr>
              <th>Question</th>
              <th>Section</th>
              <th>Response type</th>
              <th>4</th>
              <th>5</th>
              <th>6</th>
              <th>7</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Which team are you in?</td>
              <td>About you</td>
              <td>Multiple Choice</td>
              <td>Sales</td>
              <td>Support</td>
              <td>Engineering</td>
              <td>Other</td>
            </tr>
            <tr>
              <td>How supported do you feel?</td>
              <td>Your manager</td>
              <td>Scale</td>
              <td>1</td>
              <td>5</td>
              <td>Not at all</td>
              <td>Fully</td>
            </tr>
            <tr>
              <td>Would you recommend us?</td>
              <td>Your manager</td>
              <td>Yes/No</td>
              <td colSpan={4} />
            </tr>
            <tr>
              <td>What could we do better?</td>
              <td />
              <td>Open Text</td>
              <td colSpan={4} />
            </tr>
          </tbody>
        </table>

        <div className="format-dialog-actions">
          <button type="button" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </dialog>
  );
}
